(() => {
  'use strict';
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Named materials keep the outer case white while the interior catches the RGB.
  function illuminate(model, active = () => !model.paused) {
    if (!model || model.dataset.rgbReady) return;
    model.dataset.rgbReady = 'true';
    let materials = [], visible = true, last = 0, phase = 0;
    const collect = () => {
      materials = (model.model?.materials || []).filter(m => /RGB|diffuser/.test(m.name));
      model.dataset.rgbMaterials = String(materials.length);
    };
    model.addEventListener('load', collect);
    collect();
    if ('IntersectionObserver' in window) new IntersectionObserver(entries => { visible = entries[0].isIntersecting; }).observe(model);
    function frame(time) {
      if (!model.isConnected) return;
      requestAnimationFrame(frame);
      if (time-last < 50) return;
      const delta = Math.min(time-last, 100); last=time;
      if (document.hidden || !visible || !materials.length) return;
      const running = active();
      if (running) phase += delta / 16000;
      else if (model.dataset.rgbPainted) return;
      materials.forEach((m, i) => {
        const hue = phase + i * .095;
        const c = [0, 2/3, 1/3].map(offset => .5 + .5*Math.cos((hue+offset)*Math.PI*2));
        const wash = /wash/.test(m.name), blades = /blades/.test(m.name);
        m.setEmissiveFactor(c.map(v => v * (wash ? .08 : blades ? .1 : .8)));
        m.pbrMetallicRoughness.setBaseColorFactor([...c.map(v => wash ? .72+v*.15 : blades ? .22+v*.38 : .1+v*.7),1]);
      });
      model.dataset.rgbPainted = 'true';
      model.dataset.rgbPhase = phase.toFixed(3);
    }
    requestAnimationFrame(frame);
  }
  window.GroovyIlluminate = illuminate;
  function phoneTilt(root, model) {
    const button = root.querySelector('[data-gk-tilt]');
    const note = root.querySelector('[data-gk-tilt-status]');
    const sensor = window.DeviceOrientationEvent;
    if (!button || !sensor || !window.isSecureContext || !matchMedia('(pointer: coarse)').matches) return;
    const needsPermission = typeof sensor.requestPermission === 'function';
    let enabled = false, ready = false, visible = false, origin = null, base = null;
    let frame = 0, latest = null, timeout = 0, pending = false, version = 0;
    const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
    function recenter() { origin = null; base = null; latest = null; cancelAnimationFrame(frame); frame = 0; }
    function sync() {
      button.textContent = enabled ? button.dataset.stopLabel : button.dataset.startLabel;
      button.setAttribute('aria-pressed', String(enabled));
      button.disabled = pending;
    }
    function stop(message = '') {
      ++version; enabled = false; pending = false;
      window.removeEventListener('deviceorientation', orient);
      clearTimeout(timeout); recenter(); sync(); note.textContent = message;
    }
    function paint() {
      frame = 0;
      if (!enabled || !ready || !visible || document.hidden || !latest || !base) return;
      const angle = Number(window.screen?.orientation?.angle ?? window.orientation ?? 0) * Math.PI / 180;
      const dx = clamp(latest.gamma - origin.gamma, -35, 35);
      const dy = clamp(((latest.beta - origin.beta + 540) % 360) - 180, -35, 35);
      const horizontal = dx * Math.cos(angle) + dy * Math.sin(angle);
      const vertical = dy * Math.cos(angle) - dx * Math.sin(angle);
      const theta = base.theta + clamp(horizontal * .65, -22, 22);
      const phi = clamp(base.phi + clamp(vertical * .45, -15, 15), 25, 140);
      model.cameraOrbit = `${theta.toFixed(2)}deg ${phi.toFixed(2)}deg ${base.radius}m`;
    }
    function orient(event) {
      if (!enabled || !ready || !visible || document.hidden || !Number.isFinite(event.beta) || !Number.isFinite(event.gamma)) return;
      clearTimeout(timeout);
      if (!origin) {
        const orbit = model.getCameraOrbit();
        if (![orbit.theta, orbit.phi, orbit.radius].every(Number.isFinite)) return;
        base = {theta:orbit.theta * 180 / Math.PI, phi:orbit.phi * 180 / Math.PI, radius:orbit.radius};
        origin = {beta:event.beta, gamma:event.gamma};
        model.autoRotate = false;
        const rotate = root.querySelector('[data-gk-rotate]');
        rotate.textContent = rotate.dataset.startLabel; rotate.setAttribute('aria-pressed','false');
        note.textContent = button.dataset.activeLabel;
      }
      latest = {beta:event.beta, gamma:event.gamma};
      if (!frame) frame = requestAnimationFrame(paint);
    }
    async function enable(request = false) {
      if (enabled || pending) return;
      const attempt = ++version;
      pending = true; sync();
      try {
        if (needsPermission) {
          if (!request) { pending = false; sync(); return; }
          const permission = await sensor.requestPermission();
          if (attempt !== version) return;
          if (permission !== 'granted') { stop(button.dataset.fallbackLabel); return; }
        }
        if (attempt !== version) return;
        enabled = true; pending = false; recenter(); sync();
        note.textContent = button.dataset.activeLabel;
        window.addEventListener('deviceorientation', orient, {passive:true});
        timeout = setTimeout(() => { if (enabled && !origin && visible && !document.hidden) stop(button.dataset.fallbackLabel); }, 6000);
      } catch { if (attempt === version) stop(button.dataset.fallbackLabel); }
    }
    button.hidden = false;
    button.addEventListener('click', () => enabled ? stop() : enable(true));
    model.addEventListener('load', () => { ready = true; if (!needsPermission && !reduce) enable(); });
    model.addEventListener('pointerdown', () => { if (enabled) stop(); }, {passive:true});
    root.querySelector('[data-gk-rotate]').addEventListener('click', () => stop());
    root.querySelector('[data-gk-reset]').addEventListener('click', recenter);
    const visibility = () => recenter();
    document.addEventListener('visibilitychange', visibility);
    window.addEventListener('orientationchange', recenter);
    let observer;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; recenter(); }, {threshold:.1});
      observer.observe(root);
    } else visible = true;
    const pagehide = () => stop();
    window.addEventListener('pagehide', pagehide);
    const unload = event => {
      if (!event.target.contains(root)) return;
      stop(); observer?.disconnect();
      document.removeEventListener('visibilitychange', visibility);
      window.removeEventListener('orientationchange', recenter);
      window.removeEventListener('pagehide', pagehide);
      document.removeEventListener('shopify:section:unload', unload);
    };
    document.addEventListener('shopify:section:unload', unload);
    sync();
  }
  function modelShowcase(root) {
    if (root.dataset.sceneReady) return;
    root.dataset.sceneReady = 'true';
    const model = root.querySelector('model-viewer');
    const motion = root.querySelector('[data-gk-motion]');
    const rotate = root.querySelector('[data-gk-rotate]');
    const status = root.querySelector('[data-gk-model-status]');
    let running = !reduce, visible = false, started = false;
    illuminate(model, () => running && visible);
    function sync() {
      if (running && visible) model.play?.(); else model.pause?.();
      motion.textContent = running ? motion.dataset.pauseLabel : motion.dataset.runLabel;
      motion.setAttribute('aria-pressed', String(running));
    }
    async function load() {
      if (started) return;
      started = true;
      try {
        if (!customElements.get('model-viewer')) await import(root.dataset.modelLibrary);
        model.setAttribute('src', model.dataset.src);
      } catch { status.textContent = status.dataset.errorLabel; }
    }
    model.addEventListener('load', () => {status.hidden = true; sync();});
    model.addEventListener('error', () => {status.hidden = false; status.textContent = status.dataset.errorLabel;});
    motion.addEventListener('click', () => {running = !running; sync();});
    rotate.addEventListener('click', () => {
      model.autoRotate = !model.autoRotate;
      rotate.textContent = model.autoRotate ? rotate.dataset.stopLabel : rotate.dataset.startLabel;
      rotate.setAttribute('aria-pressed', String(model.autoRotate));
    });
    root.querySelector('[data-gk-reset]').addEventListener('click', () => {model.cameraOrbit = '-40deg 75deg auto'; model.fieldOfView = 'auto';});
    phoneTilt(root, model);
    if ('IntersectionObserver' in window) new IntersectionObserver(entries => {
      visible = entries[0].isIntersecting;
      if (visible) load();
      sync();
    }, {threshold:.05}).observe(root);
    else {visible = true; load();}
    sync();
  }
  function scene(root) {
    if (root.hasAttribute('data-gk-model-only')) {modelShowcase(root); return;}
    if (root.dataset.sceneReady) return;
    root.dataset.sceneReady = 'true';
    const slides = [...root.querySelectorAll('[data-gk-photo]')];
    const buttons = [...root.querySelectorAll('[data-gk-photo-select]')];
    const display = root.querySelector('.gk-photo-display');
    const zoom = root.querySelector('[data-gk-photo-zoom]');
    const model = root.querySelector('model-viewer');
    const modelButton = root.querySelector('[data-gk-model-toggle]');
    const fans = root.querySelector('[data-gk-fans]');
    illuminate(model, () => !model.paused && root.classList.contains('is-model-view'));
    function syncFans() { if(fans) { fans.textContent = model?.paused ? fans.dataset.runLabel : fans.dataset.pauseLabel; fans.setAttribute('aria-pressed', String(!model?.paused)); } }
    fans?.addEventListener('click', () => { if(model?.paused) model.play?.(); else model?.pause?.(); syncFans(); });
    async function showModel() {
      if (!model) return;
      const open = !root.classList.contains('is-model-view');
      root.classList.toggle('is-model-view', open);
      modelButton?.setAttribute('aria-pressed', String(open));
      if (open && !reduce) model.play?.();
      else model.pause?.();
      syncFans();
      if (open && !model.dataset.started) {
        model.dataset.started = 'true';
        try {
          if (!customElements.get('model-viewer')) {
            if (root.dataset.modelLibrary) await import(root.dataset.modelLibrary);
            else if (window.Shopify?.loadFeatures) await new Promise((resolve, reject) => window.Shopify.loadFeatures([{name:'model-viewer-ui',version:'1.0',onLoad:error=>error?reject(error):resolve()}]));
          }
          if (model.dataset.src) model.setAttribute('src', model.dataset.src);
        } catch {
          root.classList.remove('is-model-view');
          modelButton?.setAttribute('aria-pressed', 'false');
          model.dataset.started = '';
        }
      }
    }
    modelButton?.addEventListener('click', showModel);
    model?.addEventListener('load', () => { if (!reduce && root.classList.contains('is-model-view')) model.play?.(); syncFans(); });
    if (model && !reduce && !navigator.connection?.saveData && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => { if(entries[0].isIntersecting) { if(!root.classList.contains('is-model-view')) showModel(); observer.disconnect(); } }, {threshold:.15});
      observer.observe(root);
    }
    model?.addEventListener('error', () => {
      root.classList.remove('is-model-view');
      modelButton?.setAttribute('aria-pressed', 'false');
    });

    let selected = 0;
    function choose(index) {
      root.classList.remove('is-model-view');
      modelButton?.setAttribute('aria-pressed', 'false');
      model?.pause?.();
      selected = (index + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === selected);
        slide.setAttribute('aria-hidden', String(i !== selected));
      });
      buttons.forEach((button, i) => button.setAttribute('aria-pressed', String(i === selected)));
    }
    buttons.forEach((button, i) => button.addEventListener('click', () => choose(i)));
    display?.addEventListener('keydown', e => {
      if (!root.classList.contains('is-model-view') && (e.key === 'ArrowRight' || e.key === 'ArrowLeft')) {
        e.preventDefault(); choose(selected + (e.key === 'ArrowRight' ? 1 : -1));
      }
      if (e.key === 'Escape' && root.classList.contains('is-zoomed')) zoom?.click();
    });
    zoom?.addEventListener('click', () => {
      const open = root.classList.toggle('is-zoomed');
      zoom.setAttribute('aria-pressed', String(open));
      zoom.textContent = open ? zoom.dataset.closeLabel : zoom.dataset.openLabel;
    });
    if (!reduce && matchMedia('(pointer: fine)').matches) {
      display?.addEventListener('pointermove', e => {
        const rect = display.getBoundingClientRect();
        display.style.setProperty('--photo-x', `${((e.clientX - rect.left) / rect.width - .5) * 8}px`);
        display.style.setProperty('--photo-y', `${((e.clientY - rect.top) / rect.height - .5) * 6}px`);
      });
      display?.addEventListener('pointerleave', () => {
        display.style.setProperty('--photo-x', '0px'); display.style.setProperty('--photo-y', '0px');
      });
    }
  }
  function init(scope=document) {
    const photoDialog=scope.querySelector('#gk-photo-viewer'), opener=scope.querySelector('[data-gk-gallery-open]');
    if(photoDialog&&opener&&!opener.dataset.ready){
      opener.dataset.ready='true';
      const photos=[...scope.querySelectorAll('[data-gk-gallery-thumb]')].map(b=>b.dataset.gkGalleryThumb);let index=0;
      const show=()=>{photoDialog.querySelector('img').src=photos[index];photoDialog.querySelector('[data-gk-photo-count]').textContent=`${index+1} / ${photos.length}`;};
      opener.addEventListener('click',()=>{index=Math.max(0,photos.indexOf(scope.querySelector('[data-gk-gallery-main]').src));show();photoDialog.showModal();});
      photoDialog.querySelector('[data-gk-close-photo]').addEventListener('click',()=>photoDialog.close());
      photoDialog.addEventListener('close',()=>opener.focus());
      const step=n=>{index=(index+n+photos.length)%photos.length;show();};
      photoDialog.querySelectorAll('[data-gk-photo-step]').forEach(b=>b.addEventListener('click',()=>step(Number(b.dataset.gkPhotoStep))));
      photoDialog.addEventListener('keydown',e=>{if(e.key==='ArrowRight'){e.preventDefault();step(1);}if(e.key==='ArrowLeft'){e.preventDefault();step(-1);}});
    }
    scope.querySelectorAll('[data-gk-scene]').forEach(scene);
    if(!reduce&&'IntersectionObserver'in window){document.documentElement.classList.add('gk-motion');const observer=new IntersectionObserver(entries=>entries.forEach(e=>{if(e.isIntersecting){e.target.classList.remove('gk-pending');observer.unobserve(e.target);}}),{threshold:.07});scope.querySelectorAll('.gk-reveal').forEach((e,i)=>{if(e.dataset.revealReady)return;e.dataset.revealReady='true';const rect=e.getBoundingClientRect();if(rect.top<innerHeight&&rect.bottom>0)return;e.style.setProperty('--gk-delay',`${i%3*55}ms`);e.classList.add('gk-pending');observer.observe(e);});}
    scope.querySelectorAll('[data-gk-workload-filter]').forEach(button=>{if(button.dataset.ready)return;button.dataset.ready='true';button.addEventListener('click',()=>{const root=button.closest('[data-gk-workloads]'),category=button.dataset.gkWorkloadFilter;root.querySelectorAll('[data-gk-workload-filter]').forEach(b=>b.setAttribute('aria-pressed',String(b===button)));root.querySelectorAll('[data-gk-workload]').forEach(c=>c.hidden=category!=='all'&&c.dataset.gkWorkload!==category);});});
    scope.querySelectorAll('[data-gk-variant]').forEach(select=>{if(select.dataset.ready)return;select.dataset.ready='true';select.addEventListener('change',()=>{const option=select.selectedOptions[0],card=select.closest('.gk-card'),currency=select.dataset.currency||'CAD',money=n=>new Intl.NumberFormat('en-CA',{style:'currency',currency}).format(Number(n)/100);card.querySelector('[data-gk-price]').textContent=money(option.dataset.price);card.dataset.price=Number(option.dataset.price)/(card.closest('[data-preview]')?100:1);const compare=card.querySelector('[data-gk-compare]');compare.hidden=!(Number(option.dataset.compare)>Number(option.dataset.price));compare.textContent=money(option.dataset.compare||0);const specs=card.querySelector('[data-gk-specs]');if(specs){const rest=(specs.dataset.gkSpecsFixed||'').split('·').slice(1).join('·').trim(),gpu=option.textContent.replace(/\([^)]*\)|\[[^\]]*\]/g,'').trim();specs.textContent=gpu+(rest?' · '+rest:'');}card.querySelectorAll('a[href]').forEach(a=>a.href=option.dataset.url);});});
    scope.querySelectorAll('[data-gk-nav] details').forEach(d=>{if(d.dataset.ready)return;d.dataset.ready='true';d.addEventListener('toggle',()=>{if(d.open)scope.querySelectorAll('[data-gk-nav] details').forEach(other=>{if(other!==d)other.open=false;});});});
    scope.querySelectorAll('[data-gk-search]').forEach(search=>{if(search.dataset.ready)return;search.dataset.ready='true';search.addEventListener('input',()=>{const root=search.closest('[data-gk-catalog]'),term=search.value.toLowerCase().trim();let count=0;root.querySelectorAll('.gk-card').forEach(c=>{c.hidden=!c.textContent.toLowerCase().includes(term);if(!c.hidden)count++;});const countEl=root.querySelector('[data-gk-results]');if(countEl)countEl.textContent=`${count} products`;root.querySelector('[data-gk-empty]')?.toggleAttribute('hidden',count>0);});});
    scope.querySelectorAll('[data-gk-sort]').forEach(select=>{if(select.dataset.ready)return;select.dataset.ready='true';select.addEventListener('change',()=>{if(select.closest('[data-preview]')){const root=select.closest('[data-gk-catalog]'),grid=root.querySelector('.gk-product-grid');[...grid.querySelectorAll('.gk-card')].sort((a,b)=>select.value==='price-descending'?Number(b.dataset.price)-Number(a.dataset.price):Number(a.dataset.price)-Number(b.dataset.price)).forEach(c=>grid.append(c));}else{const url=new URL(location.href);url.searchParams.set('sort_by',select.value);location.href=url;}});});
    scope.querySelectorAll('[data-gk-gallery-thumb]').forEach(button=>button.addEventListener('click',()=>{const root=button.closest('.gk-product-gallery');root.querySelector('[data-gk-gallery-main]').src=button.dataset.gkGalleryThumb;root.querySelectorAll('[data-gk-gallery-thumb]').forEach(b=>b.setAttribute('aria-pressed',String(b===button)));}));
    scope.querySelectorAll('[data-gk-qty]').forEach(button=>button.addEventListener('click',()=>{const input=button.closest('.gk-product-qty').querySelector('input');input.value=Math.max(1,Math.min(Number(input.max)||99,Number(input.value)+Number(button.dataset.gkQty)));}));
    scope.querySelectorAll('[data-preview-buy]').forEach(button=>button.addEventListener('click',()=>{const dialog=document.querySelector('#gk-preview-cart');if(dialog)dialog.showModal();}));
    scope.querySelectorAll('[data-gk-close-dialog]').forEach(button=>button.addEventListener('click',()=>button.closest('dialog').close()));
  }
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>init());else init();
  document.addEventListener('shopify:section:load',event=>init(event.target));
})();
